import { Head, router, useForm } from "@inertiajs/react"

import AgentForm from "@/components/agents/agent-form"
import RecordForm from "@/components/record-form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import RecordPageHeader from "@/components/ui/record-page-header"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AdminLayout from "@/layouts/admin-layout"
import { adminAgentPath, adminAgentsPath, editAdminAgentPath } from "@/routes"
import type {
  Account,
  Agent,
  AvailableModel,
  AvailableSkill,
  BreadcrumbItem,
} from "@/types"

interface ArchetypeRef {
  uid: string
  name: string
  slug: string | null
}

interface VoiceOption {
  id: string
  name: string
  description: string
}

// Custom agent edit gets the full form. Standard agent instances only
// expose status — everything else is owned by the archetype, so a smaller
// shape is rendered without the AgentForm tabs.
type StandardAgent = Pick<Agent, "uid" | "name" | "state"> & {
  account: { uid: string; name: string }
  agent_archetype: ArchetypeRef
  custom_prompt_instructions?: string | null
  voice?: string | null
  retail_price_override_cents?: number | null
  standard_retail_price_cents?: number | null
}

export default function EditAgent({
  agent,
  accounts,
  available_skills = [],
  available_models = [],
  voices = [],
}: {
  agent: Agent | StandardAgent
  accounts?: Account[]
  available_skills?: AvailableSkill[]
  available_models?: AvailableModel[]
  voices?: VoiceOption[]
}) {
  const isStandard = "agent_archetype" in agent && agent.agent_archetype != null

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Agents", href: adminAgentsPath() },
    {
      title: agent.name,
      href: adminAgentPath({ id: agent.uid }),
    },
    {
      title: "Edit",
      href: editAdminAgentPath({ id: agent.uid }),
    },
  ]

  if (isStandard) {
    return (
      <StandardAgentEdit
        agent={agent as StandardAgent}
        voices={voices}
        breadcrumbs={breadcrumbs}
      />
    )
  }

  return (
    <CustomAgentEdit
      agent={agent as Agent}
      accounts={accounts ?? []}
      availableSkills={available_skills}
      availableModels={available_models}
      breadcrumbs={breadcrumbs}
    />
  )
}

const STATE_OPTIONS = [
  { value: "provisional", label: "Provisional" },
  { value: "draft", label: "Draft" },
  { value: "operational", label: "Operational" },
  { value: "degraded", label: "Degraded" },
  { value: "paused", label: "Paused" },
  { value: "retired", label: "Retired" },
]

function StandardAgentEdit({
  agent,
  voices,
  breadcrumbs,
}: {
  agent: StandardAgent
  voices: VoiceOption[]
  breadcrumbs: BreadcrumbItem[]
}) {
  const { data, setData, patch, processing } = useForm({
    agent: {
      state: agent.state,
      custom_prompt_instructions: agent.custom_prompt_instructions ?? "",
      voice: agent.voice ?? "",
      retail_price_override:
        agent.retail_price_override_cents != null
          ? String(agent.retail_price_override_cents / 100)
          : "",
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(adminAgentPath({ id: agent.uid }))
  }

  function handleCancel() {
    router.visit(adminAgentPath({ id: agent.uid }))
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${agent.name}`} />
      <div className="flex h-full w-full flex-col gap-6 p-4">
        <div className="mx-auto w-full max-w-2xl">
          <RecordPageHeader title={agent.name} state={agent.state} />
        </div>
        <RecordForm
          submitLabel="Save"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          processing={processing}
        >
          <div className="grid gap-2">
            <Label>Archetype</Label>
            <p className="text-sm">{agent.agent_archetype.name}</p>
          </div>

          <div className="grid gap-2">
            <Label>Account</Label>
            <p className="text-sm">{agent.account.name}</p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="state">Status</Label>
            <Select
              value={data.agent.state}
              onValueChange={(val) =>
                setData("agent", {
                  ...data.agent,
                  state: val as Agent["state"],
                })
              }
            >
              <SelectTrigger id="state" className="w-full">
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {STATE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Standard agents only expose status. Other configuration lives on
              the archetype.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="voice">Voice</Label>
            <Select
              value={data.agent.voice}
              onValueChange={(val) =>
                setData("agent", { ...data.agent, voice: val })
              }
            >
              <SelectTrigger id="voice" className="w-full">
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent>
                {voices.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} — {v.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Sets the agent&rsquo;s Retell voice and syncs immediately, without
              the client going through setup.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="retail_price_override">Retail price override</Label>
            <Input
              id="retail_price_override"
              type="number"
              min="0"
              step="0.01"
              value={data.agent.retail_price_override}
              onChange={(e) =>
                setData("agent", {
                  ...data.agent,
                  retail_price_override: e.target.value,
                })
              }
              placeholder={
                agent.standard_retail_price_cents != null
                  ? `Standard: $${(agent.standard_retail_price_cents / 100).toFixed(2)}`
                  : "No standard price configured"
              }
            />
            <p className="text-muted-foreground text-xs">
              Overrides the standard retail price for this agent only (in
              dollars). Leave blank to use the standard price. Applies at
              checkout/activation.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="custom_prompt_instructions">
              Custom prompt instructions
            </Label>
            <Textarea
              id="custom_prompt_instructions"
              rows={5}
              value={data.agent.custom_prompt_instructions}
              onChange={(e) =>
                setData("agent", {
                  ...data.agent,
                  custom_prompt_instructions: e.target.value,
                })
              }
              placeholder="Appended to the generated receptionist prompt for this agent."
            />
            <p className="text-muted-foreground text-xs">
              Added to the end of the agent&rsquo;s Retell prompt and synced
              immediately. Leave blank to use the base generated prompt. Not
              visible to end users.
            </p>
          </div>
        </RecordForm>
      </div>
    </AdminLayout>
  )
}

function CustomAgentEdit({
  agent,
  accounts,
  availableSkills,
  availableModels,
  breadcrumbs,
}: {
  agent: Agent
  accounts: Account[]
  availableSkills: AvailableSkill[]
  availableModels: AvailableModel[]
  breadcrumbs: BreadcrumbItem[]
}) {
  const { data, setData, patch, processing, errors } = useForm({
    agent: {
      name: agent.name,
      description: agent.description,
      problem_solved: agent.problem_solved,
      summary: agent.summary,
      state: agent.state,
      account_uid: agent.account_uid ?? "",
      hourly_rate: agent.hourly_rate ?? undefined,
      manual_task_time_in_minutes:
        agent.manual_task_time_in_minutes ?? undefined,
      external_id: agent.external_id ?? undefined,
      slug: agent.slug ?? "",
      system_prompt: agent.system_prompt ?? "",
      model_id: agent.model_id ?? undefined,
      launch_title: agent.launch_title ?? "",
      launch_prompt: agent.launch_prompt ?? "",
      launch_icon: agent.launch_icon ?? "",
      agent_metrics_attributes:
        agent.agent_metrics?.map((m) => ({
          id: (m as any).id,
          uid: m.uid,
          label: m.label,
          description: m.description || "",
          data_source_identifier: m.data_source_identifier,
        })) || [],
      skills: agent.skills || [],
      integration_kinds: agent.agent_integrations || [],
      resources_by_kind: agent.agent_resources || {},
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(adminAgentPath({ id: agent.uid }), { forceFormData: true })
  }

  function handleCancel() {
    router.visit(adminAgentPath({ id: agent.uid }))
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${agent.name}`} />
      <div className="flex h-full flex-col p-4">
        <RecordForm
          submitLabel="Save"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          processing={processing}
        >
          <AgentForm
            data={data}
            setData={setData}
            errors={errors}
            accounts={accounts}
            availableSkills={availableSkills}
            availableModels={availableModels}
            existingMetrics={agent.agent_metrics}
            promptVersions={agent.agent_prompt_versions}
            mode="edit"
          />
        </RecordForm>
      </div>
    </AdminLayout>
  )
}
